import type { ReactNode } from 'react'
import { Magnet, Maximize2, Redo2, Scissors, Trash2, Undo2, ZoomIn, ZoomOut } from 'lucide-react'
import { useEditorStore } from '../../../store/editorStore'
import { formatTime } from '../../../lib/time'
import { getTotalDurationSec, MAX_PX_PER_SEC, MIN_PX_PER_SEC } from '../../../lib/timelineMath'
import { TOOLBAR_H } from './constants'

function ToolbarIconButton({
  icon,
  title,
  onClick,
  disabled,
  active,
  testId,
}: {
  icon: ReactNode
  title: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`inline-flex items-center justify-center w-6 h-6 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors ${
        active ? 'bg-accent-gold/20 text-accent-gold' : 'text-text-muted'
      }`}
    >
      {icon}
    </button>
  )
}

/** task spec §2-13: タイムライン上端のツールバー（32px）。ショートカットはツールチップに併記。 */
export function Toolbar() {
  const undo = useEditorStore((s) => s.undo)
  const redo = useEditorStore((s) => s.redo)
  const historyCanUndo = useEditorStore((s) => s.historyCanUndo)
  const historyCanRedo = useEditorStore((s) => s.historyCanRedo)
  const splitAtPlayhead = useEditorStore((s) => s.splitAtPlayhead)
  const splitAndKeep = useEditorStore((s) => s.splitAndKeep)
  const removeSelected = useEditorStore((s) => s.removeSelected)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const snapEnabled = useEditorStore((s) => s.snapEnabled)
  const toggleSnap = useEditorStore((s) => s.toggleSnap)
  const resetZoom = useEditorStore((s) => s.resetZoom)
  const zoomPxPerSec = useEditorStore((s) => s.zoomPxPerSec)
  const effectivePxPerSec = useEditorStore((s) => s.effectivePxPerSec)
  const setZoomPxPerSec = useEditorStore((s) => s.setZoomPxPerSec)
  const composition = useEditorStore((s) => s.composition)
  const currentSec = useEditorStore((s) => s.playback.currentSec)

  const totalSec = composition ? getTotalDurationSec(composition) : 0

  return (
    <div
      className="flex items-center gap-0.5 px-2 border-b border-ink-border bg-ink-surface2/60 shrink-0"
      style={{ height: TOOLBAR_H }}
      data-testid="tl-toolbar"
    >
      <ToolbarIconButton icon={<Undo2 size={14} />} title="元に戻す (⌘Z)" onClick={undo} disabled={!historyCanUndo} testId="tl-undo" />
      <ToolbarIconButton icon={<Redo2 size={14} />} title="やり直す (⇧⌘Z)" onClick={redo} disabled={!historyCanRedo} testId="tl-redo" />
      <div className="w-px h-4 bg-ink-border mx-1" />
      <ToolbarIconButton icon={<Scissors size={14} />} title="分割 (⌘B)" onClick={splitAtPlayhead} testId="tl-split" />
      <button
        type="button"
        onClick={() => splitAndKeep('left')}
        title="左を削除 (Q)"
        data-testid="tl-split-left"
        className="text-[10px] font-semibold w-6 h-6 rounded hover:bg-white/10 text-text-muted"
      >
        Q
      </button>
      <button
        type="button"
        onClick={() => splitAndKeep('right')}
        title="右を削除 (W)"
        data-testid="tl-split-right"
        className="text-[10px] font-semibold w-6 h-6 rounded hover:bg-white/10 text-text-muted"
      >
        W
      </button>
      <ToolbarIconButton
        icon={<Trash2 size={14} />}
        title="削除 (Delete)"
        onClick={removeSelected}
        disabled={selectedIds.length === 0}
        testId="tl-delete"
      />
      <div className="w-px h-4 bg-ink-border mx-1" />
      <ToolbarIconButton icon={<Magnet size={14} />} title="吸着 (N)" onClick={toggleSnap} active={snapEnabled} testId="tl-snap" />
      <ToolbarIconButton
        icon={<Maximize2 size={14} />}
        title="全体を表示 (⌘0)"
        onClick={resetZoom}
        active={zoomPxPerSec == null}
        testId="tl-fit"
      />
      <div className="flex items-center gap-1 ml-1">
        <ZoomOut size={12} className="text-text-faint shrink-0" />
        <input
          type="range"
          min={MIN_PX_PER_SEC}
          max={MAX_PX_PER_SEC}
          step={1}
          value={Math.round(effectivePxPerSec)}
          onChange={(e) => setZoomPxPerSec(Number(e.target.value))}
          className="w-20 accent-accent-blue"
          title="ズーム"
          data-testid="tl-zoom-slider"
        />
        <ZoomIn size={12} className="text-text-faint shrink-0" />
      </div>
      <div className="flex-1" />
      <div className="text-[11px] tabular-nums text-text-muted" data-testid="tl-time-readout">
        {formatTime(currentSec, true)} / {formatTime(totalSec, true)}
      </div>
    </div>
  )
}
